import React, { useState, useCallback, useMemo } from "react"
import * as tad from "tadiff"
import * as qs from "query-string"

type FormulaInputProps = {
    setExpression: (expression: tad.Expression | null) => void
}

let defaultFormula = "cos(4 * x)"
let defaultDiffBy = "x, x"

const searchObject = qs.parse(window.location.search)

if (searchObject.f && typeof searchObject.f === "string") {
    defaultFormula = decodeURIComponent(searchObject.f)
}

if (searchObject.d && typeof searchObject.d === "string") {
    defaultDiffBy = decodeURIComponent(searchObject.d)
}

const defaultExpression = tad.parseExpression(defaultFormula)

function FormulaInput(props: FormulaInputProps) {
    const [valid, setValid] = useState(true)
    const setExpression = props.setExpression

    const buildExpression = useCallback((formula: string) => {
        try {
            const expression = tad.parseExpression(formula)
            setValid(true)
            setExpression(expression)
        } catch {
            setValid(false)
            setExpression(null)
        }
    }, [setExpression, setValid])

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        buildExpression(e.target.value)
    }, [buildExpression])

    return (
        <input type="text" defaultValue={defaultFormula} onChange={onChange} style={{ color: valid ? "black" : "red", width: 500 }} />
    )
}

export default function App() {
    const [expression, setExpression] = useState<tad.Expression | null>(defaultExpression)

    const [variablesString, setVariablesString] = useState(defaultDiffBy)

    const derivativeExpression = useMemo(() => {
        if (expression) {
            const foundVariables = tad.getAllVariables(expression)

            const variablesStringSplit = variablesString
                .split(",")
                .map(s => s.trim())
                .filter(s => s !== "")

            if (variablesStringSplit.length === 0) {
                return "Enter variables seperated by comma to differentiate by."
            }

            const wantedVariables = variablesStringSplit.map(wantedVariable => foundVariables[wantedVariable])

            // Check if we have any variables not contained in the expression
            let errorVar: string | null = null
            wantedVariables.forEach((wantedVariable, index) => {
                if (wantedVariable === undefined) {
                    errorVar = variablesStringSplit[index]
                    return
                }
            })

            if (errorVar) {
                return `Unknown variable ${errorVar}.`
            }

            // Differentiate
            let derivative = expression
            while (wantedVariables.length > 0) {
                derivative = tad.getDerivativeForExpression(wantedVariables[0], tad.getAllDerivatives(derivative, new tad.Constant(1)))
                wantedVariables.splice(0, 1)
            }

            return tad.expressionToNode(derivative).toString()
        }

        return "Invalid expression"
    }, [variablesString, expression])

    return (
        <div style={{ textAlign: "center", padding: "10px", fontFamily: "Segoe UI, Arial" }}>
            <h1>Automatic differentiation</h1>
            Powered by <a href="https://github.com/RobinKa/tadiff">tadiff</a>

            <h2>Formula</h2>
            <FormulaInput setExpression={setExpression} />

            <h2>Take derivative with respect to</h2>
            <input type="text" defaultValue={defaultDiffBy} onChange={e => setVariablesString(e.target.value)} style={{ width: 500 }} />

            <h2>Derivative</h2>
            <label>{derivativeExpression}</label>
        </div>
    )
}
